
import { pipeline, env } from '@xenova/transformers';
import {handleAbort} from "./PdfParser";

env.allowLocalModels = false;
env.useBrowserCache = false;

export default class TranscribeVideoUtility {

    static audioContext;
    static transcriberInstance = null;

    static async getModelConnection(envType = 'web') {
        try {
            if (this.transcriberInstance !== null){
                return this.transcriberInstance;
            }
            let transcriberInstance;
            switch (envType) {
                case 'web':
                    transcriberInstance = await pipeline(
                        'automatic-speech-recognition',
                        'Xenova/whisper-tiny.en'
                    );
                    break;
                case 'onPrem':
                    transcriberInstance = await pipeline(
                        'automatic-speech-recognition',
                        '/models/whisper-small'
                    );
                    break;
                default:
                    throw new Error(`Unknown environment: ${envType}`);
            }
            this.transcriberInstance = transcriberInstance;
            return transcriberInstance;
        } catch (error) {
            throw error;
        }
    }

    static async extractAudioDirectly(file) {
        const videoEl = document.createElement('video');
        videoEl.src = URL.createObjectURL(file);

        await new Promise((resolve, reject) => {
            videoEl.onloadedmetadata = resolve;
            videoEl.onerror = reject;
        });

        if (!this.audioContext) {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        }

        const arrayBuffer = await file.arrayBuffer();
        const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);


        const offlineContext = new OfflineAudioContext(
            1,
            audioBuffer.duration * 16000,
            16000
        );

        const source = offlineContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(offlineContext.destination);
        source.start();

        const resampledBuffer = await offlineContext.startRendering();

        URL.revokeObjectURL(videoEl.src);
        return resampledBuffer.getChannelData(0);
    }

    static async transcribeVideo(file, abortedFiles, envType = 'web', chunkDurationSeconds = 30) {
        const sampleRate = 16000;
        const chunkSize = chunkDurationSeconds * sampleRate;

        const float32Audio = await this.extractAudioDirectly(file)

        const transcriber = await this.getModelConnection(envType);

        let result = []
        for (let i = 0; i < float32Audio.length; i += chunkSize) {
            if (handleAbort(file, abortedFiles, true)) {
                return [];
            }
            const audioChunk = float32Audio.slice(i, i + chunkSize);
            const timeOffset = i / sampleRate;



            const batchResult = await transcriber(audioChunk, {
                return_timestamps: true
            });


            const adjustedChunks = batchResult.chunks.map(chunk => ({
                start: chunk.timestamp[0] + timeOffset,
                end: chunk.timestamp[0] + timeOffset,
                text: chunk.text
            }));

            result.push(...adjustedChunks);
        }
        return result
    }

    static formatTimestampedText(timestamps) {
        return timestamps.map(t => {
            const startTime = t.start;
            const endTime = t.end;
            return `start:${startTime}, end:${endTime} text:${t.text}`;
        }).join('\n');
    }

    static createEmbeddableChunks(timestampedChunks, abortedFiles, targetWords = 150) {
        const embeddableChunks = [];
        let currentChunk = {
            text: '',
            wordCount: 0,
            timestamps: [],
            start: null,
            end: null
        };

        for (const chunk of timestampedChunks) {
            if (handleAbort({name:''}, abortedFiles, true)) {
                return [];
            }
            if (currentChunk.start === null) {
                currentChunk.start = chunk.start;
            }

            const words = chunk.text.trim().split(/\s+/).filter(w => w.length > 0);
            currentChunk.text += (currentChunk.text ? ' ' : '') + chunk.text.trim();
            currentChunk.wordCount += words.length;
            currentChunk.timestamps.push(chunk);
            currentChunk.end = chunk.end;


            if (currentChunk.wordCount >= targetWords) {
                embeddableChunks.push({
                    textForEmbedding: currentChunk.text,
                    textWithTimestamps: this.formatTimestampedText(currentChunk.timestamps),
                    start: currentChunk.start,
                    end: currentChunk.end
                });

                currentChunk = {
                    text: '',
                    wordCount: 0,
                    timestamps: [],
                    start: null,
                    end: null
                };
            }
        }

        if (currentChunk.text) {
            embeddableChunks.push({
                textForEmbedding: currentChunk.text,
                textWithTimestamps: this.formatTimestampedText(currentChunk.timestamps),
                start: currentChunk.start,
                end: currentChunk.end
            });
        }

        return embeddableChunks;
    }
    static formatTime(seconds) {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    }


    static async decodeWavToFloat32(uint8Array) {
        if (!this.audioContext) {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        }

        const arrayBuffer = uint8Array.buffer.slice(
            uint8Array.byteOffset,
            uint8Array.byteOffset + uint8Array.byteLength
        );

        const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
        return audioBuffer.getChannelData(0);
    }

}
